import axios, { CancelTokenSource } from 'axios';
import { useContext, useRef } from 'react';
import useSWR from 'swr';
import {AssemblyWithMetadata, AXIOS_CANCEL_MSG, Page} from '../api/models';
import { ConfiguratorContext } from '../context';
import {AssemblyFilterOptions, FilteredAssembliesRequest} from "../api";
import {SorterResult, SortOrder} from "antd/es/table/interface";


type AssemblyListProps<T> = {
  filterOptions?: AssemblyFilterOptions
  current?: number
  pageSize?: number
  sorter?: SorterResult<T> | SorterResult<T>[]
  disabled?:boolean
};

const DEFAULT_PAGE_SIZE = 20;

const DEFAULT_SORT = {
  columnKey: 'assemblyCategory.name',
  order: 'ascend' as SortOrder,
}

export const useAssembliesWithMetadata = (props: AssemblyListProps<AssemblyWithMetadata>) => {

  const cancelTokenSourceRef = useRef<CancelTokenSource>();
  const configurator = useContext(ConfiguratorContext);

  const fetcher = async ( props:AssemblyListProps<AssemblyWithMetadata>  ) : Promise<Page<AssemblyWithMetadata> | undefined> => {

    if ( props.disabled ) return;

    if ( cancelTokenSourceRef.current ) {
      cancelTokenSourceRef.current.cancel( AXIOS_CANCEL_MSG );
    }
    const cancelSource = axios.CancelToken.source();
    cancelTokenSourceRef.current = cancelSource;

    const flatSorter = props.sorter && [props.sorter].flat()
        .filter(v=>!!Object.values(v || {}).filter(v=>v).length)
        .map( x => {
          //remove annoying parameters
          return { columnKey: x?.columnKey, order: x?.order};
        })
    const sorter = !!flatSorter?.length ? flatSorter :  DEFAULT_SORT;

    const columnFilterValues = Object.values(props.filterOptions?.columnFilterValues || {}).length ? props.filterOptions?.columnFilterValues : undefined;

    const opts:FilteredAssembliesRequest = {
      filterOptions: {
        ...props.filterOptions,
        columnFilterValues: columnFilterValues ? JSON.stringify(columnFilterValues) : undefined
      },
      pageable: {
        page: (props.current || 1 ) - 1,
        size:props.pageSize || DEFAULT_PAGE_SIZE,
        // @ts-ignore
        sorter
      }
    }

    try {
      const resp = await configurator.api.fetchAssembliesWithMetadata(opts, cancelSource.token )
      cancelTokenSourceRef.current = undefined;

      return resp.data;
    }
    catch(e: any) {
      const id = e.response?.data?.message || e.message ;
      if ( id === AXIOS_CANCEL_MSG ) return;
      throw e;
    }
  }

  // Use SWR for data fetching
  return useSWR([
    'fetchAssembliesWithMetadata',
    props
  ],
    ([_k, p]) => fetcher(p),
    {
      //maybe unnecessary
      revalidateOnFocus: false,
      //maybe unnecessary
      dedupingInterval:1 
    }
  );
};


export default useAssembliesWithMetadata;

